<template>
  <div>
    <div :class='[{ "is-active": isOpen }, "modal"]'>
      <div class='modal-background'></div>

      <div class='modal-card'>
        <header class='modal-card-head'>
          <h2 class='modal-card-title'>Smarthost Editor</h2>
          <button class='delete' :disabled='testing' aria-label='close' @click='onCloseEditor'></button>
        </header>

        <section class='modal-card-body'>
          <ErrorNotification v-if='savingErrors.length' :messages='savingErrors' @clear='onClearError("saving")' />
          <TestingErrorsNotification v-if='testingErrors.length' :messages='testingErrors' @clear='onClearError("testing")' />

          <div class='columns' style='margin-bottom: 0'>
            <div class='field column is-four-fifths'>
              <label class='label'>Host</label>
              <div class='control'>
                <input
                  class='input'
                  type='text'
                  placeholder='Host'
                  ref='hostInput'
                  v-model='smarthost.host'
                />
              </div>
            </div>

            <div class='field column'>
              <label class='label'>Port</label>
              <div class='control'>
                <input
                  class='input'
                  type='number'
                  placeholder='Port'
                  v-model.number='smarthost.port'
                />
              </div>
            </div>
          </div>

          <div class='field'>
            <label :class='["checkbox", $style.checkbox]'>
              <input type='checkbox' v-model='isEU' />
              Test via EU
            </label>
          </div>

          <div class='field'>
            <label :class='["label", $style.inlineLabel]'>Preference</label>
            <span :class='$style.labelMessage'>Smarthosts with the lowest preference are used first.</span>
            <div class='control'>
              <input
                class='input'
                type='number'
                placeholder='Preference'
                v-model.number='smarthost.priority'
              />
            </div>
          </div>

          <div class='field'>
            <label :class='["checkbox", $style.checkbox, { [$style.disabledLabel]: hasCredentials }]'>
              <input type='checkbox' v-model='smarthost.requireTLS' :disabled='hasCredentials' />
              Require TLS
            </label>
            <span :class='$style.tlsMessage' v-if='hasCredentials'>TLS is required when using credentials</span>
          </div>

          <div class='field'>
            <label class='label'>Username</label>
            <div class='control'>
              <input
                class='input'
                type='text'
                placeholder='Username'
                v-model.number='smarthost.username'
                autocomplete='off'
              />
            </div>
          </div>

          <div class='field'>
            <label class='label'>Password</label>
            <div class='control'>
              <input
                class='input'
                type='password'
                placeholder='Password'
                v-model.number='smarthost.password'
              />
            </div>
          </div>
        </section>

        <footer class='modal-card-foot'>
          <div style='width: 100%'>
            <button
              :class='[{ "is-loading": testing }, "button is-success"]'
              @click='onAddSmarthost'
            >
              Save
            </button>
            <button
              v-if='!testing'
              class='button is-cancel'
              @click='$emit("close")'
            >
              Cancel
            </button>

            <button
              v-if='isExisting'
              class='button is-danger is-outlined is-pulled-right'
              @click='isConfirmDeleteOpen = true'
            >
              Delete Smarthost
            </button>
          </div>
        </footer>

        <DeleteConfirmation
          :isOpen='isConfirmDeleteOpen'
          @confirm='onConfirmDelete'
          @cancel='isConfirmDeleteOpen = false'
        >
          <p>Are you sure you want to delete this smarthost?</p>
        </DeleteConfirmation>
      </div>
    </div>
  </div>
</template>

<script>
import { testSmarthost } from '@/request';
import ErrorNotification from '@/components/ErrorNotification.vue';
import DeleteConfirmation from '@/components/DeleteConfirmation.vue';
import TestingErrorsNotification from '@/components/TestingErrorsNotification.vue';

export default {
  name: 'SmarthostEditor',

  components: {
    ErrorNotification,
    DeleteConfirmation,
    TestingErrorsNotification
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    domain: {
      type: String,
      default: ''
    },
    selected: {
      type: Object,
      default: () => ({})
    },
    index: {
      type: Number,
      default: -1
    },
    savingErrors: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      smarthost: {
        ...this.selected
      },
      isEU: false,
      testingErrors: [],
      testing: false,
      isConfirmDeleteOpen: false
    };
  },

  mounted() {
    document.body.addEventListener('keydown', this.onKeydown);
    if (!this.smarthost.host) {
      this.$nextTick(() => {
        this.$refs.hostInput.focus();
      });
    }
  },

  unmounted() {
    document.body.removeEventListener('keydown', this.onKeydown);
  },

  computed: {
    isExisting() {
      return this.index > -1;
    },

    hasCredentials() {
      return !!(this.smarthost.username || this.smarthost.password);
    }
  },

  watch: {
    hasCredentials(value, oldValue) {
      if (value === oldValue) return;

      if (value) {
        this.smarthost.requireTLS = true;
      }
    }
  },

  methods: {
    onKeydown(e) {
      if (this.isConfirmDeleteOpen || !this.isOpen) return;

      if (e.which === 13) {
        this.onAddSmarthost();
      }

      if (e.which === 27) {
        this.onCloseEditor();
      }
    },

    onClearError(type) {
      if (type === 'testing') {
        this.testingErrors = [];
      } else if (type === 'saving') {
        this.$emit('clearErrors');
      }
    },

    onCloseEditor() {
      this.testingErrors = [];
      this.$emit('close');
    },

    onConfirmDelete() {
      this.$emit('delete');
      this.isConfirmDeleteOpen = false;
    },

    onAddSmarthost() {
      this.testing = true;
      this.testingErrors = [];
      const region = this.isEU ? 'eu' : 'us';

      if (!this.smarthost.username) {
        delete this.smarthost.username;
      }

      if (!this.smarthost.password) {
        delete this.smarthost.password;
      }

      testSmarthost(this.smarthost, region)
        .then(() => {
          this.$emit('success', this.smarthost);
        })
        .catch(err => {
          // parse and display error(s)
          this.testingErrors = JSON.parse(err.message);
        })
        .finally(() => {
          this.testing = false;
        });
    }
  }
}
</script>

<style lang="scss" module>
@import 'src/styles/variables.scss';

.disabledLabel {
  opacity: 0.5;
}

.inlineLabel {
  display: inline-block;
}

.labelMessage {
  margin-left: 60px;
}

.tlsMessage {
  margin-left: 80px;
}

</style>
